import {FormattedMessage, useIntl} from "react-intl";
import {Button, Col, Form, Input, Row, Select} from "antd";
import React, {SyntheticEvent, useContext, useEffect, useState} from "react";
import {Organization} from "../../api-client";
import {getOrganizationApi} from "../../api/apiInstances";
import {Navigate} from "react-router-dom";
import '../../assets/css/Dentobook.css';
import {CaretDownOutlined} from "@ant-design/icons";
// @ts-ignore
import {isMobileOnly} from "react-device-detect";
import FormButtons from "../common/FormButtons";
import {AlertContext} from "../../context/AlertContext";
import {ExitPromptContext} from "../../context/ExitPromptContext";
import {allCountries} from "../../i18n/countries";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";

const {Option} = Select;

export interface OrganizationFormProps {
    clinic: any;
    setEditMode?: Function;
    openModal: Function;
}

const OrganizationForm = ({clinic, setEditMode, openModal}: OrganizationFormProps) => {

    const intl = useIntl();
    const {locale} = useContext(DentobookApplicationContext);
    const {setFormDirty} = useContext(ExitPromptContext);
    const {setVisible} = useContext(AlertContext);

    const [conflictError, setConflictError] = useState(false);
    const [formClinic] = useState(clinic);
    const [organizations, setOrganizations] = useState<Organization[]>([]);

    const [done, setDone] = useState(false);

    const organizationApi = getOrganizationApi();

    const [form] = Form.useForm();

    useEffect(() => {
        organizationApi.fetchAllOrganizations('')
            .then(res => {
                setOrganizations(res.data);
            })
            .catch(err => logger.log(err));

        const userLanguage = navigator.language || navigator.languages[0];
        let detectedCountry = '';

        if (userLanguage.toLowerCase().includes('bg')) {
            detectedCountry = 'BG';
        } else {
            fetch('https://ipapi.co/json/')
                .then(response => response.json())
                .then(data => {
                    const country = data?.country;  // Using optional chaining
                    if (country) {
                        detectedCountry = data.country;
                        form.setFieldsValue({ country: detectedCountry });
                        formClinic.country = detectedCountry;
                    }
                })
                .catch(err => {
                    logger.log('Failed to detect country', err);
                });
        }

        if (detectedCountry) {
            form.setFieldsValue({ country: detectedCountry });
            formClinic.country = detectedCountry;
        }
    }, []);

    const createOrganization = () => {
        organizationApi.createOrganization(formClinic)
            .then(() => {
                setDone(true);
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            })
            .catch((err: any) => setConflictError(+err.request.status === 409));
    };

    const updateOrganization = () => {
        organizationApi.updateOrganization(clinic.id, formClinic)
            .then(() => {
                setDone(true);
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            })
            .catch((err: any) => setConflictError(+err.request.status === 409));
    };

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();

        try {
            await form.validateFields();

            if (!clinic.id) {
                if (!formClinic.parentId) {
                    createOrganization();
                }
                organizationApi.fetchOrganizationById(formClinic.parentId)
                    .then(res => {
                        formClinic.parent = res.data;
                        createOrganization();
                    })
                    .catch(err => logger.log(err));
            } else {
                if (!formClinic.parentId) {
                    updateOrganization();
                }
                organizationApi.fetchOrganizationById(formClinic.parentId)
                    .then(res => {
                        formClinic.parent = res.data;
                        updateOrganization();
                    })
                    .catch(err => logger.log(err));
            }
        } catch (err) {
            return;
        }
    };

    if (done) {
        return <Navigate to="/organizations"/>
    }

    return (
        <Form form={form} name="horizontal_login" layout="vertical" className="mt-4" onFocus={() => setFormDirty(true)}>
            <Form.Item
                label={<FormattedMessage id="name"/>}
                className={"dentobook-form-item dentobook-simple-input"}
                name="name"
                initialValue={formClinic.name}
                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
            >
                <Input
                    bordered={false}
                    className="input-ninety-percents dentobook-inputs" value={formClinic.name}
                    onChange={e => formClinic.name = e.target.value}/>
            </Form.Item>
            {conflictError ? <div className="org-conflict-text"><FormattedMessage id="organization_conflict"/></div> : null}
            <Form.Item name="parent"
                       className={"dentobook-form-item"}
                       label={<FormattedMessage id="parent_organization"/>}
                       initialValue={formClinic.parent?.name}>
                <Select
                    size={"large"}
                    className="dentobook-input input-ninety-percenst"
                    value={formClinic.parentId}
                    suffixIcon={<CaretDownOutlined/>}
                    onChange={e => formClinic.parentId = e}
                    allowClear
                >
                    {organizations
                        .filter((organization: Organization) => organization.id !== clinic.id
                            && organization.id !== clinic.parent?.id)
                        .map((organization: Organization) => (
                            <Option key={organization.id} value={organization.id}>{organization.name}</Option>
                        ))}
                </Select>
            </Form.Item>
            <Row>
                <Col flex={1}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="email"/>}
                        name="email"
                        rules={[{
                            type: "email",
                            message: <FormattedMessage id="invalid_email"/>
                        }]}
                        initialValue={formClinic.email}
                    >
                        <Input
                            className="input-ninety-percent"
                            value={formClinic.email}
                            onChange={e => formClinic.email = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="phone"/>}
                        name="phone"
                        rules={[{
                            pattern: new RegExp(/^[+()\d\s]+$/gm),
                            message: <FormattedMessage id="invalid_phone_number"/>
                        }]}
                        initialValue={formClinic.phone}
                    >
                        <Input type="tel"
                               className="dentobook-input input-ninety-percent"
                               value={formClinic.phone}
                               onChange={e => formClinic.phone = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.5}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="town"/>}
                        name="town"
                        initialValue={formClinic.town}
                    >
                        <Input
                            className="dentobook-input input-ninety-percent"
                            value={formClinic.town}
                            onChange={e => formClinic.town = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.5}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input single-select"}
                        label={<FormattedMessage id="country"/>}
                        name="country"
                        initialValue={formClinic.country}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Select
                            showSearch
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            autoComplete="new-state"
                            suffixIcon={<CaretDownOutlined/>}
                            className="dentobook-input input-ninety-percent"
                            value={formClinic.country}
                            onChange={e => formClinic.country = e}
                            filterOption={(input, option) =>
                                String(option?.children).toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={intl.formatMessage({id: 'search'})}
                        >
                            {allCountries.map(countryItem => (
                                <Select.Option key={countryItem.code}
                                               value={countryItem.code}>
                                    {locale === 'BG' ? countryItem.name_bg : countryItem.name_en}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item
                className={"dentobook-form-item dentobook-simple-input mb-112"}
                label={<FormattedMessage id="address"/>}
                name="addressTwo"
                initialValue={formClinic.address}
                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
            >
                <Input value={formClinic.address}
                       onChange={e => formClinic.address = e.target.value}/>
            </Form.Item>
            <FormButtons>
                <Button
                    className={isMobileOnly ? "dentobook-btn-secondary mobile-update-single-button" : "dentobook-btn-secondary dentobook-btn-large"}
                    htmlType="button" onClick={() => openModal()}>
                    {<FormattedMessage id="cancel"/>}
                </Button>
                <Button
                    className={isMobileOnly ? "dentobook-btn-primary mobile-update-single-button" : "dentobook-btn-primary dentobook-btn-large"}
                    onClick={handleSubmit} htmlType="submit">
                    {<FormattedMessage id="save"/>}
                </Button>
            </FormButtons>
        </Form>
    );
}

export default OrganizationForm;